import { tarihSaatBae } from './web-sitesi-bicim';
import { visaOrderHref } from './operasyon-visa-route';
import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  api,
  type DepartureList,
  type Me,
  type Money,
  type ProductList,
  type Receivables,
  type ReleaseQueue,
  type SalesReport,
  type V3OrderDetail,
  type V3OrderList,
  type V3Source,
  type WebOrderDetailRecord,
  type WebOrderDocument,
  type WebOrderDocumentList,
} from './api';
import './web-order-products.css';
import { TarihAraligiSecici } from './TarihSecici';
import { COMMERCE_METIN as METIN } from './metin/commerce';
import { ORTAK, TANIMSIZ, durumAdi, kalemTuru, para, tamSayi, tarih, tarihSaat } from './metin/ortak';

/**
 * MBDTravel v3'ü okuyan üç bölüm: Satış, Operasyon, Finans.
 *
 * Sorumluluklar ayrı olduğu için ayrılar, üç sekme derli toplu göründüğü için değil. Operasyon
 * sekmesi sipariş listesiyle değil release kuyruğuyla açılıyor: bu bölümün var olma sebebi olan
 * karar yielding, ve düz bir sipariş listesi aynı veriyi taşırken o kararı gizliyor.
 *
 * Ekranda çizilen her cümle `metin/commerce.ts` içinden geliyor; burada gömülü metin yok.
 */

/**
 * Tutar.
 *
 * Para birimi tutarın yanından okunuyor, varsayılmıyor: v3 EUR, USD ve AED gönderiyor ve
 * hepsini tek bir para birimi saymak, üç siparişin ikisinde yanlış rakam göstermek olurdu.
 */
function tutar(money: Money | null | undefined): string {
  return para(money?.amount, money?.currency);
}

/**
 * Kur oranı.
 *
 * v3 oranı ölçekli tam sayı gönderiyor, tam da hiçbir yerde yuvarlanmasın diye. Burada da
 * bölme yapılmıyor: ondalık noktası dizenin içinde kaydırılıyor, çünkü kayan noktalı bir bölme
 * son basamağı oynatabiliyor ve kur son basamağıyla anlamlı.
 */
export function formatScaled(value: number, scale: number): string {
  const asText = String(scale);
  if (!/^10*$/.test(asText)) return `${value}/${scale}`;
  const decimals = asText.length - 1;
  if (decimals === 0) return String(value);
  const digits = String(Math.abs(value)).padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  return `${value < 0 ? '-' : ''}${whole}${METIN.ondalikAyirici}${digits.slice(digits.length - decimals)}`;
}

/**
 * Kod değerinin ekran karşılığı.
 *
 * Önce v3'e özgü tabloya, sonra CRM'in ortak tablosuna bakılıyor. İkisinde de yoksa kodun
 * kendisi yazılıyor: uydurulmuş bir karşılık, eksik olandan kötüdür.
 */
function durumMetni(kod: string | null | undefined): string {
  if (!kod) return TANIMSIZ;
  return METIN.durum[kod] ?? durumAdi(kod);
}

function Provenance({ source }: { source: V3Source }) {
  return (
    <p className="card source" data-testid="v3-source">
      {METIN.kaynak.satir(
        source.system,
        source.baseUrl,
        tarihSaat(source.fetchedAt),
        tamSayi(source.elapsedMs),
        tamSayi(source.attempts),
        source.correlationId,
      )}
    </p>
  );
}

/**
 * Başarısız bir v3 okuması hiçbir zaman boş tabloya dönüşmüyor. Boş tablo "bir şey yok" diyor;
 * buradaki metin "tanımlı değil", "erişilemiyor" ve "reddedildi" arasından hangisinin
 * gerçekleştiğini söylüyor, çünkü kesintiyi "bekleyen sipariş yok" diye okuyan operatör buna
 * göre davranır.
 */
function Failure({ error }: { error: ApiError }) {
  const blocked = error.code === 'V3_NOT_CONFIGURED';
  return (
    <div className={blocked ? 'banner' : 'error'} data-testid={blocked ? 'v3-not-configured' : 'v3-error'}>
      <strong>{blocked ? METIN.hata.tanimsiz : METIN.hata.okumaBasarisiz(error.code ?? error.status)}</strong>
      {/*
        `blocked` iken aşağıdaki Türkçe açıklama zaten aynı şeyi söylüyor: `error.message` burada
        yalnız uçtan gelen İngilizce cümlenin tekrarıydı (`V3_NOT_CONFIGURED: The website is not
        configured…`), tercüme edilmemiş ve gereksiz. Başka bir hatada uçtan gelen ham mesaj hâlâ
        gösteriliyor ama `code` içinde: bu ekibin konuştuğu bir UI cümlesi değil, dış bir sistemin
        ham tanısı.
      */}
      {blocked ? null : <code>{error.message}</code>}
      {blocked ? (
        <div>
          <code>V3_BASE_URL</code> {METIN.hata.ayarVe} <code>V3_API_TOKEN</code> {METIN.hata.ayarTanimla}{' '}
          {METIN.hata.jetonOnce} <code>BACK_OFFICE_API_CLIENTS</code> {METIN.hata.jetonSonra}
        </div>
      ) : null}
      {error.correlationId ? <div>{METIN.hata.korelasyon(error.correlationId)}</div> : null}
    </div>
  );
}

/** Her panel aynı biçimde yükleniyor: tek istek, üç olası sonuç, hiçbiri sessiz değil. */
function useV3<T>(load: () => Promise<T>, deps: readonly unknown[]): {
  value: T | null;
  error: ApiError | null;
  busy: boolean;
  reload: () => void;
} {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    load()
      .then((result) => {
        if (cancelled) return;
        setValue(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setValue(null);
        setError(caught instanceof ApiError ? caught : new ApiError(0, ORTAK.hataOlustu));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // `deps` bütün hâlde değil yayılarak veriliyor: React bağımlılık dizisini eleman eleman
    // karşılaştırıyor, her çizimde yeni bir dizi sabiti verilseydi her çizimde yeniden okurdu.
  }, [...deps, nonce]);

  return { value, error, busy, reload: () => setNonce((current) => current + 1) };
}

// ---------------------------------------------------------------------------
// Operasyon: yielding
// ---------------------------------------------------------------------------

/**
 * Bir grubu pazarlığa çeviren eşik, bir kalkışta iki sipariş. Bu sayı burada seçilmedi, ürün
 * sahibinin tetikleyiciyi anlatışı bu: "aynı kalkışa iki sipariş düşerse ekip tedarikçiden
 * indirim ya da daha yüksek komisyon istiyor".
 */
const NEGOTIABLE_FROM = 2;

export function ReleaseQueuePanel() {
  const [limit, setLimit] = useState(200);
  const { value, error, busy, reload } = useV3<ReleaseQueue>(() => api.releaseQueue({ limit }), [limit]);

  return (
    <section data-testid="release-queue">
      <h2>{METIN.yielding.baslik}</h2>
      <p>{METIN.yielding.aciklama}</p>
      <label>
        {METIN.yielding.sayfaBoyu}{' '}
        <input
          data-testid="queue-limit"
          type="number"
          min={1}
          max={500}
          value={limit}
          onChange={(event) => setLimit(Number(event.target.value))}
        />
      </label>{' '}
      <button type="button" data-testid="queue-reload" onClick={reload} disabled={busy}>
        {busy ? ORTAK.yukleniyor : METIN.yielding.yenile}
      </button>

      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <p className="banner" data-testid="release-unavailable">
            <strong>{METIN.yielding.releaseYok}</strong> {value.release.reason}
          </p>
          {value.shortfall.complete ? null : (
            <p className="error" data-testid="queue-shortfall">
              {METIN.yielding.eksikSayfa(
                tamSayi(value.shortfall.missingOrders),
                tamSayi(value.page.limit),
                tamSayi(value.page.total),
              )}
            </p>
          )}
          <p data-testid="queue-total">
            {METIN.yielding.toplam(tamSayi(value.departures.length), tamSayi(value.page.total))}
          </p>
          {value.departures.map((departure) => (
            <div className="card" key={departure.departureId ?? departure.productId} data-testid="queue-departure" style={{ marginBottom: 12 }}>
              <div className="value" data-testid="queue-departure-date">
                {departure.departureOn ? tarih(departure.departureOn) : METIN.yielding.kalkisTarihiYok}
              </div>
              <div data-testid="queue-departure-product">
                {departure.productName ?? METIN.yielding.urunNumarali(departure.productId)} ·{' '}
                {METIN.yielding.tedarikci(departure.supplierCode)}
              </div>
              <div>
                <strong data-testid="queue-order-count">{tamSayi(departure.orderCount)}</strong>{' '}
                {METIN.yielding.birikimSonu(tamSayi(departure.passengerCount))}
                {departure.orderCount >= NEGOTIABLE_FROM ? (
                  <span data-testid="queue-negotiable">{METIN.yielding.pazarlik}</span>
                ) : null}
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{METIN.yielding.sutun.referans}</th>
                      <th>{METIN.yielding.sutun.olusturuldu}</th>
                      <th>{METIN.yielding.sutun.yolcu}</th>
                      <th>{METIN.yielding.sutun.toplam}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departure.orders.map((order) => (
                      <tr key={order.reference} data-testid="queue-order">
                        <td>{order.reference}</td>
                        <td>{tarihSaat(order.createdAt)}</td>
                        <td>{tamSayi(order.passengerCount)}</td>
                        {/* Grup toplamı yok: siparişler ayrı para birimlerinde olabiliyor ve
                            toplamak, yielding kararının hiç ihtiyaç duymadığı bir kur kararı
                            gerektirirdi. */}
                        <td>{tutar(order.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <Provenance source={value.source} />
        </>
      ) : null}
    </section>
  );
}

export function OrdersPanel() {
  const [status, setStatus] = useState('');
  const [reference, setReference] = useState<string | null>(null);
  const { value, error, busy } = useV3<V3OrderList>(() => api.v3Orders({ status: status || undefined, limit: 50 }), [status]);

  return (
    <section data-testid="v3-orders">
      <h2>{METIN.siparisler.baslik}</h2>
      <label>
        {ORTAK.durum}{' '}
        {/* Seçenek değerleri v3'e gittiği gibi duruyor; çevrilen yalnız ekranda okunan karşılık. */}
        <select data-testid="orders-status" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">{METIN.siparisler.tumu}</option>
          <option value="PENDING_PAYMENT">{durumMetni('PENDING_PAYMENT')}</option>
          {/* Provizyon: v3'ün `AUTHORIZED`i. Ayrı bir seçenek, çünkü ayrı bir durum. */}
          <option value="AUTHORIZED">{durumMetni('AUTHORIZED')}</option>
          <option value="PAID">{durumMetni('PAID')}</option>
          <option value="CANCELLED">{durumMetni('CANCELLED')}</option>
          <option value="REFUNDED">{durumMetni('REFUNDED')}</option>
        </select>
      </label>
      {busy ? <p>{ORTAK.yukleniyor}</p> : null}
      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{METIN.siparisler.sutun.referans}</th>
                  <th>{METIN.siparisler.sutun.durum}</th>
                  <th>{METIN.siparisler.sutun.toplam}</th>
                  <th>{METIN.siparisler.sutun.odendi}</th>
                  <th>{METIN.siparisler.sutun.yolcu}</th>
                  <th>{METIN.siparisler.sutun.tedarikci}</th>
                  <th>{METIN.siparisler.sutun.otomatikRezervasyon}</th>
                  <th>{METIN.siparisler.sutun.releaseBekliyor}</th>
                  <th>{METIN.siparisler.sutun.saga}</th>
                  <th>{METIN.siparisler.sutun.satisKodu}</th>
                </tr>
              </thead>
              <tbody>
                {value.orders.map((order) => (
                  <tr key={order.reference} data-testid="v3-order-row">
                    <td>
                      <button type="button" data-testid="v3-order-open" onClick={() => setReference(order.reference)}>
                        {order.reference}
                      </button>
                    </td>
                    <td>{durumMetni(order.status)}</td>
                    <td>{tutar(order.total)}</td>
                    <td>{tarihSaat(order.paidAt)}</td>
                    <td>{tamSayi(order.passengerCount)}</td>
                    <td>{order.supplierCodes.join(', ') || METIN.siparisler.kendiUrunu}</td>
                    {/* autoBook tedarikçinin özelliği. null, siparişin tedarikçisi yok demek. */}
                    <td>
                      {order.fulfilment.autoBook === null
                        ? TANIMSIZ
                        : order.fulfilment.autoBook
                          ? METIN.siparisler.evet
                          : METIN.siparisler.hayir}
                    </td>
                    {/* v3 nasıl türettiyse öyle gösteriliyor. "ödendi ve elle işleniyor ve
                        release edilmedi" kuralını burada yeniden hesaplamak kuralın ikinci bir
                        kopyası olurdu, ve iki kopya ayrışır. */}
                    <td data-testid="v3-order-awaiting">
                      {order.fulfilment.awaitingRelease ? METIN.siparisler.evet : METIN.siparisler.hayir}
                    </td>
                    <td>{order.fulfilment.sagaStatus ? durumMetni(order.fulfilment.sagaStatus) : METIN.siparisler.sagaBaslamadi}</td>
                    <td>{order.fulfilment.saleCode ?? TANIMSIZ}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p data-testid="v3-orders-total">
            {METIN.siparisler.sayac(tamSayi(value.orders.length), tamSayi(value.page.total))}
          </p>
          <Provenance source={value.source} />
        </>
      ) : null}
      {reference ? <OrderDetail reference={reference} onClose={() => setReference(null)} /> : null}
    </section>
  );
}

function OrderDetail({ reference, onClose }: { reference: string; onClose: () => void }) {
  const { value, error, busy } = useV3<V3OrderDetail>(() => api.v3Order(reference, 'tr'), [reference]);
  return (
    <div className="card" data-testid="v3-order-detail" style={{ marginTop: 12 }}>
      <button type="button" data-testid="v3-order-close" onClick={onClose}>
        {ORTAK.kapat}
      </button>
      <h3>{reference}</h3>
      {busy ? <p>{ORTAK.yukleniyor}</p> : null}
      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{METIN.siparisDetay.sutun.urun}</th>
                  <th>{METIN.siparisDetay.sutun.kalkis}</th>
                  <th>{METIN.siparisDetay.sutun.slot}</th>
                  <th>{METIN.siparisDetay.sutun.adet}</th>
                  <th>{METIN.siparisDetay.sutun.birimFiyat}</th>
                </tr>
              </thead>
              <tbody>
                {value.order.items.map((item) => (
                  <tr key={`${item.productId}-${item.departureId ?? 'none'}-${item.slot}`} data-testid="v3-item-row">
                    <td>{item.name ?? item.slug}</td>
                    <td>{tarih(item.departureOn)}</td>
                    <td>{item.slot}</td>
                    <td>{tamSayi(item.quantity)}</td>
                    {/* Satıldığı andaki fiyat, bugünkü katalog fiyatı değil. */}
                    <td>{tutar(item.unitPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4>{METIN.siparisDetay.yolcularBaslik}</h4>
          <p className="card source">{METIN.siparisDetay.yolcuGizlilik}</p>
          <ul>
            {value.order.passengers.map((passenger) => (
              <li key={passenger.position} data-testid="v3-passenger">
                {passenger.position}. {passenger.firstName} {passenger.lastName} ·{' '}
                {/*
                  Büyük harfe çevirerek aranıyor: v3 `Adult` gönderiyor, tablo `ADULT` tutuyor
                  ve karşılık hiçbir zaman bulunamıyordu. Kusur ölçüldü, uydurulmadı.
                */}
                {METIN.yolcuTuru[passenger.passengerType.toUpperCase()] ?? passenger.passengerType}
                {passenger.isMaster ? METIN.siparisDetay.liderYolcu : ''}
              </li>
            ))}
          </ul>
          <h4>{METIN.siparisDetay.sagaBaslik}</h4>
          <p>
            {METIN.siparisDetay.sagaOzeti(
              value.fulfilment.sagaStatus ? durumMetni(value.fulfilment.sagaStatus) : METIN.siparisler.sagaBaslamadi,
              value.fulfilment.saleCode ?? TANIMSIZ,
              tarihSaat(value.fulfilment.releasedAt),
            )}
          </p>
          <ol>
            {value.fulfilment.steps.map((step) => (
              <li key={step.position} data-testid="v3-saga-step" className={step.status === 'FAILED' ? 'error' : undefined}>
                {/* Adımın adı tedarikçi ucunun işlem adı, yani bir kod değeri: çevrilmiyor. */}
                {METIN.siparisDetay.sagaAdimi(step.step, durumMetni(step.status))}
                {step.errorCode ? METIN.siparisDetay.sagaHatasi(step.errorCode) : ''}
              </li>
            ))}
          </ol>
          <Provenance source={value.source} />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Satış
// ---------------------------------------------------------------------------

/**
 * Aralık içinde bulunulan takvim ayıyla dolduruluyor ve değiştirilebilir alanlarda gösteriliyor.
 * v3 varsayılan vermeyi reddediyor ve haklı: *gizli* bir varsayılan, raporu okuyan kişinin hangi
 * aralığın bu rakamları ürettiğini bilmemesi demek. Görünen ve değiştirilebilir bir varsayılanda
 * o sorun yok, tarihler ürettikleri rakamların yanında duruyor.
 */
function currentMonth(): { from: string; to: string } {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const pad = (value: number) => String(value).padStart(2, '0');
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return { from: `${year}-${pad(month + 1)}-01`, to: `${year}-${pad(month + 1)}-${pad(lastDay)}` };
}

export function SalesPanel() {
  const initial = currentMonth();
  const [form, setForm] = useState({ ...initial, language: 'tr', currency: 'EUR', groupBy: 'product' });
  const [applied, setApplied] = useState(form);
  const load = useCallback(() => api.salesReport({ ...applied, limit: 200 }), [applied]);
  const { value, error, busy } = useV3<SalesReport>(load, [applied]);

  return (
    <section data-testid="v3-sales">
      <h2>{METIN.satisRaporu.baslik}</h2>
      <p>
        {METIN.satisRaporu.aciklamaOnce} <em>{METIN.satisRaporu.aciklamaVurgu}</em> {METIN.satisRaporu.aciklamaSonra}
      </p>
      <form
        data-testid="sales-form"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(form);
        }}
      >
        {/* Rapor penceresi bir aralık: iki ayrı kutu yerine tek aralık seçici. Değer uca ISO
            biçiminde (`YYYY-MM-DD`) gidiyor; seçici de aynı biçimi veriyor. Uç iki ucu da dahil sayar. */}
        <label htmlFor="sales-window">{METIN.satisRaporu.donem}</label>{' '}
        <TarihAraligiSecici id="sales-window" testId="sales-window-picker" giris={form.from} cikis={form.to}
          setAralik={(from, to) => setForm((mevcut) => ({ ...mevcut, from, to }))} />{' '}
        <label>
          {METIN.satisRaporu.dil}{' '}
          <select data-testid="sales-language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
            <option value="tr">{METIN.satisRaporu.dilAdi.tr}</option>
            <option value="en">{METIN.satisRaporu.dilAdi.en}</option>
          </select>
        </label>{' '}
        <label>
          {METIN.satisRaporu.paraBirimi}{' '}
          {/* Para birimi kodları çevrilmiyor: EUR her dilde EUR. */}
          <select data-testid="sales-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="AED">AED</option>
          </select>
        </label>{' '}
        <label>
          {METIN.satisRaporu.gruplama}{' '}
          <select data-testid="sales-groupby" value={form.groupBy} onChange={(e) => setForm({ ...form, groupBy: e.target.value })}>
            <option value="product">{METIN.satisRaporu.gruplamaAdi.product}</option>
            <option value="departure">{METIN.satisRaporu.gruplamaAdi.departure}</option>
          </select>
        </label>{' '}
        <button type="submit" data-testid="sales-run" disabled={busy}>
          {busy ? ORTAK.yukleniyor : METIN.satisRaporu.calistir}
        </button>
      </form>

      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <div className="cards">
            <div className="card">
              <div>{METIN.satisRaporu.kartSiparis}</div>
              <div className="value" data-testid="sales-orders">{tamSayi(value.totals.orderCount)}</div>
            </div>
            <div className="card">
              <div>{METIN.satisRaporu.kartAdet}</div>
              <div className="value" data-testid="sales-units">{tamSayi(value.totals.unitCount)}</div>
            </div>
            <div className="card">
              <div>{METIN.satisRaporu.kartGelir}</div>
              <div className="value" data-testid="sales-revenue">{tutar(value.totals.revenue)}</div>
              <div className="source">{METIN.satisRaporu.gelirNotu}</div>
            </div>
            <div className="card">
              <div>{METIN.satisRaporu.kartIade}</div>
              <div className="value" data-testid="sales-refunded">{tutar(value.totals.refunded)}</div>
              <div className="source">
                {METIN.satisRaporu.iadeNotu(
                  tamSayi(value.totals.refundedOrderCount),
                  tamSayi(value.totals.refundedUnitCount),
                )}
              </div>
            </div>
          </div>
          <p data-testid="sales-window">
            {METIN.satisRaporu.aralik(
              tarih(value.window.from),
              tarih(value.window.to),
              METIN.satisRaporu.gruplamaAdi[value.window.groupBy] ?? value.window.groupBy,
            )}
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{METIN.satisRaporu.sutun.urun}</th>
                  {value.window.groupBy === 'departure' ? <th>{METIN.satisRaporu.sutun.kalkis}</th> : null}
                  <th>{METIN.satisRaporu.sutun.siparis}</th>
                  <th>{METIN.satisRaporu.sutun.adet}</th>
                  <th>{METIN.satisRaporu.sutun.gelir}</th>
                  <th>{METIN.satisRaporu.sutun.iade}</th>
                </tr>
              </thead>
              <tbody>
                {value.rows.map((row) => (
                  <tr key={`${row.productId}-${row.departureId ?? 'all'}`} data-testid="sales-row">
                    {/* Boş bir ad, ürünün o dilde içeriği yok demek. v3 başka bir dile
                        düşmüyor, bu hücre de düşmüyor. */}
                    <td>
                      {row.name ??
                        METIN.satisRaporu.icerikYok(
                          row.slug,
                          METIN.satisRaporu.dilAdi[applied.language] ?? applied.language,
                        )}
                    </td>
                    {value.window.groupBy === 'departure' ? <td>{tarih(row.departureOn)}</td> : null}
                    <td>{tamSayi(row.orderCount)}</td>
                    <td>{tamSayi(row.unitCount)}</td>
                    <td>{tutar(row.revenue)}</td>
                    <td>{tutar(row.refunded)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {value.rows.length === 0 ? <p data-testid="sales-empty">{METIN.satisRaporu.bos}</p> : null}
          <Provenance source={value.source} />
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Finans
// ---------------------------------------------------------------------------

export function FinancePanel() {
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const { value, error, busy } = useV3<Receivables>(
    () => api.receivables({ invoiceStatus: invoiceStatus || undefined, limit: 50 }),
    [invoiceStatus],
  );

  return (
    <section data-testid="v3-receivables">
      <h2>{METIN.finans.baslik}</h2>
      <p>{METIN.finans.aciklama}</p>
      <label>
        {METIN.finans.faturaDurumu}{' '}
        <select data-testid="receivables-status" value={invoiceStatus} onChange={(event) => setInvoiceStatus(event.target.value)}>
          <option value="">{METIN.finans.tumu}</option>
          <option value="NONE">{durumMetni('NONE')}</option>
          <option value="PENDING">{durumMetni('PENDING')}</option>
          <option value="ISSUED">{durumMetni('ISSUED')}</option>
          <option value="FAILED">{durumMetni('FAILED')}</option>
        </select>
      </label>
      {busy ? <p>{ORTAK.yukleniyor}</p> : null}
      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{METIN.finans.sutun.referans}</th>
                  <th>{METIN.finans.sutun.siparis}</th>
                  <th>{METIN.finans.sutun.odeme}</th>
                  <th>{METIN.finans.sutun.siparisToplami}</th>
                  <th>{METIN.finans.sutun.odendi}</th>
                  <th>{METIN.finans.sutun.fatura}</th>
                  <th>{METIN.finans.sutun.faturaTutari}</th>
                  <th>{METIN.finans.sutun.kur}</th>
                </tr>
              </thead>
              <tbody>
                {value.receivables.map((row) => (
                  <tr key={row.reference} data-testid="receivable-row">
                    <td>{row.reference}</td>
                    <td>{durumMetni(row.orderStatus)}</td>
                    {/* AUTHORIZED, CAPTURED değildir: provizyon alınmış para hesaptaki para
                        değil. İkisini "ödendi" diye birleştirmek, elde olmayan parayı saymak
                        olurdu. */}
                    <td data-testid="receivable-payment">
                      {row.paymentStatus ? durumMetni(row.paymentStatus) : METIN.finans.odemeKaydiYok}
                      {row.paymentStatus === 'AUTHORIZED' ? METIN.finans.provizyonNotu : ''}
                    </td>
                    <td>{tutar(row.orderTotal)}</td>
                    <td>{tarihSaat(row.paidAt)}</td>
                    <td data-testid="receivable-invoice">
                      {durumMetni(row.invoice.status)}
                      {row.invoice.number ? ` · ${row.invoice.number}` : ''}
                      {row.invoice.failureReason ? ` · ${row.invoice.failureReason}` : ''}
                    </td>
                    {/* Her zaman AED: siparişin donmuş kurundan türüyor ve bir daha
                        hesaplanmıyor. */}
                    <td>{tutar(row.invoice.amount ?? null)}</td>
                    <td>
                      {METIN.finans.kurSatiri(
                        formatScaled(row.fx.eurUsdScaled, row.fx.scale),
                        tarih(row.fx.rateDate),
                        row.fx.source,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p data-testid="receivables-total">
            {METIN.finans.sayac(tamSayi(value.receivables.length), tamSayi(value.page.total))}
          </p>
          <Provenance source={value.source} />
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Katalog
// ---------------------------------------------------------------------------

export function CataloguePanel() {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const { value, error, busy } = useV3<ProductList>(() => api.catalogue({ language: 'tr', currency: 'EUR', limit: 100 }), []);

  return (
    <section data-testid="v3-catalogue">
      <h2>{METIN.katalog.baslik}</h2>
      <p>{METIN.katalog.aciklama}</p>
      {busy ? <p>{ORTAK.yukleniyor}</p> : null}
      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{METIN.katalog.sutun.ad}</th>
                  <th>{METIN.katalog.sutun.tur}</th>
                  <th>{METIN.katalog.sutun.baslangicFiyati}</th>
                  <th>{METIN.katalog.sutun.yayimlandi}</th>
                  <th>{METIN.katalog.sutun.kalkislar}</th>
                </tr>
              </thead>
              <tbody>
                {value.products.map((product) => (
                  <tr key={product.slug} data-testid="catalogue-row">
                    <td>{product.name}</td>
                    <td>{kalemTuru(product.kind)}</td>
                    <td>{tutar(product.fromPrice)}</td>
                    <td>{tarihSaat(product.publishedAt)}</td>
                    <td>
                      <button type="button" data-testid="catalogue-departures" onClick={() => setOpenSlug(product.slug)}>
                        {METIN.katalog.goster}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p data-testid="catalogue-total">{METIN.katalog.sayac(tamSayi(value.page.total))}</p>
          <Provenance source={value.source} />
        </>
      ) : null}
      {openSlug ? <Departures slug={openSlug} onClose={() => setOpenSlug(null)} /> : null}
    </section>
  );
}

function Departures({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { value, error } = useV3<DepartureList>(() => api.catalogueDepartures(slug, { language: 'tr', currency: 'EUR' }), [slug]);
  return (
    <div className="card" data-testid="catalogue-departure-list">
      <button type="button" onClick={onClose}>
        {ORTAK.kapat}
      </button>
      <h4>{slug}</h4>
      {error ? <Failure error={error} /> : null}
      <ul>
        {value?.departures.map((departure) => (
          <li key={departure.id} data-testid="catalogue-departure">
            {tarih(departure.departureOn)}:{' '}
            {departure.prices
              .map((price) => METIN.katalog.kalkisFiyati(price.slot, para(price.amount, price.currency)))
              .join(' · ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Web kayıtları (denetim maddesi B5)
//
// Yukarıdaki dört panel v3'e her defasında yeniden soruyor; bu panel v3'ün CRM'e bildirdiği ve
// `WebshopService`in CRM'in kendi veritabanına yazdığı asıl kaydı okuyor
// (`api/src/modules/webshop/webshop.module.ts`, `WebshopReadController`, izin `web_intake:read`).
// O yüzden `Provenance` yok: burada bir v3 çağrısının kökeni değil, CRM'in kendi satırı gösteriliyor.
// ---------------------------------------------------------------------------




/**
 * Denetim madde B5, detay eksiği: liste yalnız özeti gösteriyordu, operatör kalemlere ve açılan
 * yerine getirme görevlerine bakamıyordu. `api.webOrder` CRM'in kendi veritabanını okuyor
 * (`WebshopReadController`, izin `web_intake:read`), v3'e sorulmuyor; bu yüzden `Provenance` yok.
 */
export function WebOrderDetail(props: { me: Me; reference: string; onClose?: () => void }) {
  // Every reference owns its own request state; a reused drawer cannot expose the previous order.
  return <WebOrderDetailContent key={props.reference} {...props} />;
}

function WebOrderDetailContent({ me, reference, onClose }: { me: Me; reference: string; onClose?: () => void }) {
  const { value: received, error, busy } = useV3<WebOrderDetailRecord>(() => api.webOrder(reference), [reference]);
  const value = received?.reference === reference ? received : null;
  const M = METIN.webKayitlari.siparisler.detay;

  return (
    <div className="card" data-testid="webshop-order-detail" style={{ marginTop: 12 }}>
      {onClose ? <button type="button" data-testid="webshop-order-detail-close" onClick={onClose}>{ORTAK.kapat}</button> : null}
      <h4>{reference}</h4>
      {busy || (!value && !error) ? <p role="status">{ORTAK.yukleniyor}</p> : null}
      {error ? <Failure error={error} /> : null}
      {value ? (
        <>
          <dl className="web-order-product-facts" data-testid="webshop-order-summary">
            <div><dt>{M.ozet.durum}</dt><dd>{durumMetni(value.state)}</dd></div>
            <div><dt>{M.ozet.tarih}</dt><dd>{tarihSaatBae(value.placedAt)}</dd></div>
            <div><dt>{M.ozet.odeme}</dt><dd>{value.paidAt ? tarihSaatBae(value.paidAt) : TANIMSIZ}</dd></div>
            <div><dt>{M.ozet.toplam}</dt><dd>{para(value.totalMinor, value.currencyCode)}</dd></div>
          </dl>
          {/* Özel talep kalemlerin üstünde: operasyonun tedarikçiye iletmesi gereken bir istek ve
              kalem tablosunun altında kalırsa gözden kaçıyor. */}
          <h5>{M.ozelTalepBaslik}</h5>
          {value.customerRequest ? (
            <p data-testid="webshop-order-customer-request" style={{ whiteSpace: 'pre-wrap' }}>
              {value.customerRequest}
            </p>
          ) : (
            <p data-testid="webshop-order-customer-request-none">{M.ozelTalepYok}</p>
          )}
          {value.invoice ? (
            <>
              <h5>{M.kurumsalFaturaBaslik}</h5>
              <dl data-testid="webshop-order-invoice">
                <dt>{M.kurumsalFatura.firma}</dt>
                <dd>{value.invoice.companyName}</dd>
                <dt>{M.kurumsalFatura.vergiDairesi}</dt>
                <dd>{value.invoice.taxOffice}</dd>
                <dt>{M.kurumsalFatura.vergiNo}</dt>
                <dd>{value.invoice.taxNumber}</dd>
                <dt>{M.kurumsalFatura.adres}</dt>
                <dd>{value.invoice.invoiceAddress}</dd>
              </dl>
            </>
          ) : null}
          {me.permissions.includes('fulfilment:write') && value.lines.some(line => line.productKind === 'VISA') && visaOrderHref(reference) ? <p><a data-testid="webshop-order-visa-link" href={visaOrderHref(reference)!}>{M.vizeOperasyon}</a></p> : null}
          <h5>{M.kalemlerBaslik}</h5>
          {value.lines.length === 0 ? <p>{M.kalemYok}</p> : <div className="web-order-products">
            {value.lines.map(line => <article className="web-order-product" key={line.itemReference} data-testid="webshop-order-line-row">
              <h6>{line.title?.trim() || M.urunAdiYok}</h6>
              <dl className="web-order-product-facts">
                <div><dt>{M.kalemSutun.adet}</dt><dd>{tamSayi(line.quantity)}</dd></div>
                <div><dt>{M.kalemSutun.birimFiyat}</dt><dd>{para(line.unitAmountMinor, value.currencyCode)}</dd></div>
                <div><dt>{M.kalemSutun.kalkis}</dt><dd>{tarih(line.departureOn)}</dd></div>
              </dl>
              <details><summary>{M.kayitAyrintilari}</summary><dl className="web-order-product-facts">
                <div><dt>{M.kalemReferansi}</dt><dd>{line.itemReference}</dd></div>
                <div><dt>{M.urunKodu}</dt><dd>{line.productId}</dd></div>
              </dl></details>
            </article>)}
          </div>}
          <h5>{M.gorevlerBaslik}</h5>
          {value.tasks.length === 0 ? <p>{M.gorevYok}</p> : <div className="web-order-products">
            {value.tasks.map((task, index) => {
              const line = value.lines.find(item => item.itemReference === task.itemReference);
              return <article className="web-order-product" key={`${task.itemReference}-${index}`} data-testid="webshop-order-task-row">
                <h6>{line?.title?.trim() || M.urunAdiYok}</h6>
                <dl className="web-order-product-facts">
                  <div><dt>{M.gorevSutun.durum}</dt><dd>{durumMetni(task.state)}</dd></div>
                  <div><dt>{M.gorevSutun.adet}</dt><dd>{tamSayi(task.quantity)}</dd></div>
                  <div><dt>{M.gorevSutun.kalkis}</dt><dd>{tarih(task.departureOn)}</dd></div>
                  <div><dt>{M.gorevSutun.acildi}</dt><dd>{tarihSaat(task.openedAt)}</dd></div>
                  <div><dt>{M.gorevSutun.tamamlandi}</dt><dd>{tarihSaat(task.completedAt)}</dd></div>
                </dl>
                <details><summary>{M.kayitAyrintilari}</summary><dl className="web-order-product-facts">
                  <div><dt>{M.kalemReferansi}</dt><dd>{task.itemReference}</dd></div>
                  <div><dt>{M.urunKodu}</dt><dd>{task.productId}</dd></div>
                </dl></details>
              </article>;
            })}
          </div>}
          {me.permissions.includes('document:read') ? (
            <WebOrderDocuments
              reference={reference}
              lines={value.lines.map((line) => line.itemReference)}
              canWrite={me.permissions.includes('document:write')}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Müşteriye hiçbir zaman gösterilmeyen türler; sunucu da reddediyor (`v3-belge-kurallari.ts`). */
const MUSTERIYE_KAPALI_TURLER: readonly string[] = ['PAYMENT_EVIDENCE', 'CONTRACT'];
const WEB_BELGE_TURLERI = ['VOUCHER', 'SUPPLIER_CONFIRMATION', 'OTHER', 'PAYMENT_EVIDENCE', 'CONTRACT'] as const;

/** Varsayılan: voucher ve tedarikçi teyidi açık, diğerleri kapalı. */
function varsayilanGorunurluk(kind: string): boolean {
  return kind === 'VOUCHER' || kind === 'SUPPLIER_CONFIRMATION';
}

/**
 * Web siparişinin belgeleri (30.09.2026). Ürün Sahibi: "tur siparişinde üye tarafında
 * konfirmasyon dosyasını Görüntüle / İndir olmalı." Belge CRM'de kalıyor; müşteriye açık olanlar
 * üye portalına gönderiliyor ve her satır gönderimin durumunu söylüyor: gönderildi, bekliyor ya
 * da hata. Hata satırında "Yeniden gönder" var.
 */
function WebOrderDocuments({ reference, lines, canWrite }: { reference: string; lines: readonly string[]; canWrite: boolean }) {
  const { value, error, busy, reload } = useV3<WebOrderDocumentList>(() => api.webOrderDocuments(reference), [reference]);
  const M = METIN.webKayitlari.siparisler.detay.belgeler;
  const [kind, setKind] = useState<string>('VOUCHER');
  const [line, setLine] = useState('');
  const [visible, setVisible] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [replacing, setReplacing] = useState<WebOrderDocument | null>(null);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = async (work: () => Promise<unknown>) => {
    setWorking(true);
    setActionError(null);
    try {
      await work();
      reload();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setWorking(false);
    }
  };

  const upload = () =>
    act(async () => {
      if (!file) throw new Error(M.dosyaSec);
      const contentBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(M.dosyaOkunamadi));
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.readAsDataURL(file);
      });
      await api.uploadWebOrderDocument({
        webOrderReference: reference,
        ...(line ? { webOrderItemReference: line } : {}),
        ...(replacing ? { replacesDocumentId: replacing.id } : {}),
        kind,
        customerVisible: MUSTERIYE_KAPALI_TURLER.includes(kind) ? false : visible,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      });
      setFile(null);
      setReplacing(null);
    });

  const kapali = MUSTERIYE_KAPALI_TURLER.includes(kind);

  return (
    <section data-testid="webshop-order-documents">
      <h5>{M.baslik}</h5>
      <p>{M.aciklama}</p>
      {error ? <Failure error={error} /> : null}
      {value && !value.v3Push.enabled ? (
        <p className="banner" data-testid="webshop-order-documents-push-off">
          {M.gonderimKapali} <code>{M.gonderimAyari}</code>
        </p>
      ) : null}
      {value && value.v3Push.enabled && !value.v3Push.configured ? (
        <p className="banner" data-testid="webshop-order-documents-push-unconfigured">{M.v3Tanimsiz}</p>
      ) : null}
      {actionError ? (
        <p className="error" data-testid="webshop-order-documents-error">
          {actionError}
        </p>
      ) : null}
      {busy && !value ? <p>{ORTAK.yukleniyor}</p> : null}
      {value && value.items.length === 0 ? <p data-testid="webshop-order-documents-empty">{M.yok}</p> : null}
      {value && value.items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{M.sutun.dosya}</th>
                <th>{M.sutun.tur}</th>
                <th>{M.sutun.kalem}</th>
                <th>{M.sutun.musteri}</th>
                <th>{M.sutun.durum}</th>
                <th>{M.sutun.yuklendi}</th>
                <th>{M.sutun.islem}</th>
              </tr>
            </thead>
            <tbody>
              {value.items.map((doc) => {
                const turAcilabilir = !MUSTERIYE_KAPALI_TURLER.includes(doc.kind);
                const hata = doc.v3Push !== null && (doc.v3Push.state === 'FAILED' || (doc.v3Push.state !== 'DONE' && doc.v3Push.lastErrorCode !== null));
                return (
                  <tr key={doc.id} data-testid="webshop-order-document-row" data-document-id={doc.id}>
                    <td>
                      {doc.fileName}
                      {doc.deleted ? <strong> ({M.silindi})</strong> : null}
                    </td>
                    <td>{M.tur[doc.kind] ?? doc.kind}</td>
                    <td>{doc.webOrderItemReference ?? M.tumSiparis}</td>
                    <td>
                      <input
                        type="checkbox"
                        data-testid="webshop-order-document-visible"
                        aria-label={M.sutun.musteri}
                        checked={doc.customerVisible}
                        disabled={!canWrite || working || doc.deleted || !turAcilabilir}
                        onChange={(event) => {
                          const next = event.target.checked;
                          void act(() => api.setWebOrderDocumentVisibility(doc.id, next));
                        }}
                      />
                    </td>
                    <td data-testid="webshop-order-document-push-state" data-state={doc.v3Push?.state ?? 'NONE'}>
                      {doc.v3Push ? doc.v3Push.label : M.musteriyeKapali}
                      {hata && doc.v3Push?.lastError ? (
                        <div className="error">
                          {M.sonHata}: <code>{doc.v3Push.lastError}</code>
                        </div>
                      ) : null}
                    </td>
                    <td>{tarihSaat(doc.uploadedAt)}</td>
                    <td>
                      {doc.deleted ? null : (
                        <button type="button" data-testid="webshop-order-document-download" onClick={() => void act(() => api.downloadTaskDocument(doc.id, doc.fileName))}>
                          {M.indir}
                        </button>
                      )}{' '}
                      {canWrite && doc.v3Push && doc.v3Push.state !== 'DONE' ? (
                        <button type="button" data-testid="webshop-order-document-retry" disabled={working} onClick={() => void act(() => api.retryWebOrderDocumentPush(doc.id))}>
                          {M.yenidenGonder}
                        </button>
                      ) : null}{' '}
                      {canWrite && !doc.deleted ? (
                        <>
                          <button
                            type="button"
                            data-testid="webshop-order-document-replace"
                            disabled={working}
                            onClick={() => {
                              setReplacing(doc);
                              setKind(doc.kind);
                              setVisible(doc.customerVisible);
                              setLine(doc.webOrderItemReference ?? '');
                            }}
                          >
                            {M.yeniSurum}
                          </button>{' '}
                          <button
                            type="button"
                            data-testid="webshop-order-document-delete"
                            disabled={working}
                            onClick={() => {
                              if (window.confirm(M.silOnay)) void act(() => api.deleteWebOrderDocument(doc.id));
                            }}
                          >
                            {M.sil}
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {canWrite ? (
        <div data-testid="webshop-order-document-upload">
          <h6>{M.yukleBaslik}</h6>
          {replacing ? (
            <p>
              {M.yeniSurumNotu(replacing.fileName)}{' '}
              <button type="button" onClick={() => setReplacing(null)}>
                {M.yeniSurumIptal}
              </button>
            </p>
          ) : null}
          <label>
            {M.yukleTur}{' '}
            <select
              data-testid="webshop-order-document-kind"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value);
                setVisible(varsayilanGorunurluk(event.target.value));
              }}
            >
              {WEB_BELGE_TURLERI.map((kod) => (
                <option key={kod} value={kod}>
                  {M.tur[kod] ?? kod}
                </option>
              ))}
            </select>
          </label>{' '}
          <label>
            {M.yukleKalem}{' '}
            <select data-testid="webshop-order-document-line" value={line} onChange={(event) => setLine(event.target.value)}>
              <option value="">{M.tumSiparis}</option>
              {lines.map((itemReference) => (
                <option key={itemReference} value={itemReference}>
                  {itemReference}
                </option>
              ))}
            </select>
          </label>{' '}
          <label>
            <input
              type="checkbox"
              data-testid="webshop-order-document-upload-visible"
              checked={kapali ? false : visible}
              disabled={kapali}
              onChange={(event) => setVisible(event.target.checked)}
            />{' '}
            {M.yukleMusteri}
          </label>
          {kapali ? <small> {M.yukleMusteriYasak}</small> : null}{' '}
          <label>
            {M.yukleDosya}{' '}
            <input
              type="file"
              data-testid="webshop-order-document-file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>{' '}
          <button type="button" data-testid="webshop-order-document-submit" disabled={working} onClick={() => void upload()}>
            {working ? M.yukleniyor : M.yukle}
          </button>
        </div>
      ) : null}
    </section>
  );
}











// ---------------------------------------------------------------------------
